import React, { useEffect, useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Flame, AlertTriangle, TrendingUp } from 'lucide-react';
const STORAGE_KEY = 'mp_urgency_end';
const DURATION_MS = 24 * 60 * 60 * 1000; // 24h
function getEndTime(): number {
  if (typeof window === 'undefined') return Date.now() + DURATION_MS;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed;
    }
    const end = Date.now() + DURATION_MS;
    window.sessionStorage.setItem(STORAGE_KEY, String(end));
    return end;
  } catch {
    return Date.now() + DURATION_MS;
  }
}
function formatPart(n: number) {
  return n.toString().padStart(2, '0');
}
export function UrgencyBanner() {
  const [endTime] = useState<number>(() => getEndTime());
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, endTime - now);
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  // Simulated slot fill — grows slowly toward 95% over the 24h window
  const elapsed = DURATION_MS - remaining;
  const slotsTaken = Math.min(95, 72 + elapsed / DURATION_MS * 23);
  return <div className="mb-5 sm:mb-8" data-id="element-310">
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4
    }} className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-red-500 shadow-2xl shadow-red-500/30" style={{
      background: 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 50%, #991b1b 100%)'
    }} data-id="element-311">
        {/* Animated pulse overlay */}
        <motion.div aria-hidden animate={{
        opacity: [0.15, 0.35, 0.15]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute inset-0 bg-red-500 pointer-events-none" data-id="element-312" />

        {/* Diagonal stripes pattern */}
        <div aria-hidden className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 10px, rgba(255,255,255,0.4) 10px 12px)'
      }} data-id="element-313" />

        <div className="relative p-3 sm:p-5" data-id="element-314">
          {/* Top row: badge + countdown */}
          <div className="flex items-center justify-between gap-2 sm:gap-3 mb-2.5 sm:mb-3" data-id="element-315">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0" data-id="element-316">
              <motion.div animate={{
              rotate: [0, -8, 8, 0]
            }} transition={{
              duration: 1,
              repeat: Infinity,
              repeatDelay: 0.8
            }} className="flex-shrink-0" data-id="element-317">
                <Flame className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-300 fill-yellow-300" data-id="element-318" />
              </motion.div>
              <span className="text-yellow-300 font-black uppercase tracking-wider text-[10px] sm:text-sm whitespace-nowrap" data-id="element-319">
                ¡Última oportunidad!
              </span>
            </div>

            {/* Countdown HH:MM:SS */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0" data-id="element-320">
              {[{
              value: hours,
              label: 'h'
            }, {
              value: minutes,
              label: 'm'
            }, {
              value: seconds,
              label: 's'
            }].map((part, i, arr) => <Fragment key={part.label} data-id="element-321">
                  <div className="flex flex-col items-center bg-black/40 backdrop-blur rounded-md px-1.5 py-0.5 sm:px-2 sm:py-1 min-w-[28px] sm:min-w-[40px]" data-id="element-322">
                    <span className="font-black text-white text-sm sm:text-xl leading-none tabular-nums" data-id="element-323">
                      {formatPart(part.value)}
                    </span>
                    <span className="text-[8px] sm:text-[10px] text-red-200 uppercase font-bold leading-none mt-0.5" data-id="element-324">
                      {part.label}
                    </span>
                  </div>
                  {i < arr.length - 1 && <span className="text-yellow-300 font-black text-sm sm:text-xl" data-id="element-325">
                      :
                    </span>}
                </Fragment>)}
            </div>
          </div>

          {/* Warning message */}
          <div className="flex items-start gap-1.5 sm:gap-2 mb-2.5 sm:mb-3" data-id="element-326">
            <AlertTriangle className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-300 flex-shrink-0 mt-0.5" data-id="element-327" />
            <p className="text-white text-[11px] sm:text-base font-bold leading-snug" data-id="element-328">
              Cuando se agoten los cupos el precio sube{' '}
              <span className="text-yellow-300 inline-flex items-center gap-0.5" data-id="element-329">
                +$15.000
                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-330" />
              </span>{' '}
              y{' '}
              <span className="underline decoration-yellow-300 decoration-2 underline-offset-2" data-id="element-331">
                NO VUELVE A BAJAR
              </span>
              .
            </p>
          </div>

          {/* Slot fill bar */}
          <div data-id="element-332">
            <div className="flex items-center justify-between text-[9px] sm:text-xs font-bold text-red-100 mb-1 uppercase tracking-wider" data-id="element-333">
              <span data-id="element-334">Cupos vendidos</span>
              <motion.span key={Math.round(slotsTaken)} initial={{
              opacity: 0.5
            }} animate={{
              opacity: 1
            }} className="text-yellow-300" data-id="element-335">
                {Math.round(slotsTaken)}%
              </motion.span>
            </div>
            <div className="w-full h-2 sm:h-2.5 bg-black/40 rounded-full overflow-hidden relative" data-id="element-336">
              <motion.div initial={{
              width: '0%'
            }} animate={{
              width: `${slotsTaken}%`
            }} transition={{
              duration: 1.2,
              ease: 'easeOut'
            }} className="h-full bg-gradient-to-r from-yellow-400 via-orange-400 to-red-300 rounded-full relative" data-id="element-337">
                {/* shimmer */}
                <motion.div aria-hidden animate={{
                x: ['-100%', '200%']
              }} transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent" data-id="element-338" />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}